'use client';

import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// Tono semántico de la tarjeta: SIEMPRE se refuerza con ícono + texto, nunca
// solo con color (accesibilidad — no depender del color como único indicador).
export type TonoStat = 'primary' | 'amber' | 'red' | 'slate';

const TONOS: Record<TonoStat, string> = {
  primary: 'bg-primary/10 text-primary',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-destructive/10 text-destructive',
  slate: 'bg-muted text-muted-foreground',
};

// Mismas clases base que <Card> (rounded-xl border bg-card shadow-sm) para que
// se vea idéntica en cualquier vista del sitio, sea clicable o no.
const BASE =
  'flex flex-col gap-3 rounded-xl border bg-card p-5 text-card-foreground shadow-sm transition-all';

export default function StatCard({
  label,
  value,
  icon: Icon,
  tone = 'primary',
  hint,
  loading = false,
  onClick,
}: {
  label: string;
  value: string | number;
  icon: LucideIcon;
  tone?: TonoStat;
  hint?: string;
  loading?: boolean;
  onClick?: () => void;
}) {
  const contenido = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', TONOS[tone])}>
          <Icon className="size-4.5" />
        </span>
      </div>
      {loading ? (
        <div className="h-8 w-16 animate-pulse rounded-md bg-muted" />
      ) : (
        <div className="text-2xl font-semibold tracking-tight">{value}</div>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          BASE,
          'cursor-pointer text-left hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
      >
        {contenido}
      </button>
    );
  }

  return <div className={BASE}>{contenido}</div>;
}
